import { useEffect, useState, useRef } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, AlertCircle } from "lucide-react";
import NeedHelp from "@/components/NeedHelp";
import logo from "./AuthLogo.png";

// Regex for ISO 3166-1 alpha-3 country codes (3 uppercase letters)
const countryISORegex = /^[A-Z]{3}$/;

// Minimum age calculation function
const isAtLeast16 = (dateStr) => {
  const today = new Date();
  const birthDate = new Date(dateStr);
  const age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  const dayDiff = today.getDate() - birthDate.getDate();

  if (
    age > 16 ||
    (age === 16 && (monthDiff > 0 || (monthDiff === 0 && dayDiff >= 0)))
  ) {
    return true;
  }

  return false;
};

const registerSchema = z.object({
  email: z.string().email("Please enter a valid email"),
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  phone: z.string().optional(),
  street: z.string().optional(),
  buildingName: z.string().optional(),
  buildingNumber: z.string().optional(),
  flatNumber: z.string().optional(),
  town: z.string().min(1, "Town is required"),
  country: z
    .string()
    .regex(
      countryISORegex,
      "Country must be a valid ISO alpha-3 code like USA or GBR"
    ),
  postalCode: z.string().optional(),
  dateOfBirth: z
    .string()
    .min(1, "Date of birth is required")
    .refine(isAtLeast16, {
      message: "You must be at least 16 years old",
    }),
});

export default function RegisterPage() {
  const [, navigate] = useLocation();
  const { user, registerMutation } = useAuth();
  const [postalCode, setPostalCode] = useState("");
  const [addresses, setAddresses] = useState([]);
  const [noResultsFound, setNoResultsFound] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const timeoutRef = useRef(null);
  const formRef = useRef(null);

  const savedUser = JSON.parse(sessionStorage.getItem("verifiedUser") || "{}");

  const registerForm = useForm({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      email: savedUser.email || "",
      firstName: savedUser.firstName || "",
      lastName: savedUser.lastName || "",
      phone: savedUser.phone || "",
      street: "",
      buildingName: "",
      buildingNumber: "",
      flatNumber: "",
      town: "",
      country: "",
      postalCode: "",
      dateOfBirth: "",
    },
  });

  useEffect(() => {
    // Clear addresses and reset states if postal code is too short
    if (postalCode.length < 2) {
      setAddresses([]);
      setNoResultsFound(false);
      setIsSearching(false);
      return;
    }

    // Reset states when user starts typing
    setNoResultsFound(false);
    setIsSearching(false);

    // Clear previous timeout if user types again
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set a new debounce timeout
    timeoutRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        console.log(`Fetching addresses for postcode: ${postalCode}`);

        const response = await fetch(
          `/api/get-address?postcode=${encodeURIComponent(postalCode)}`,
          {
            method: "GET",
            credentials: "include",
          }
        );

        const result = await response.json();

        if (!response.ok) {
          console.error("Error fetching address:", result.error);
          setAddresses([]);
          setNoResultsFound(true);
          setIsSearching(false);
          return;
        }

        const foundAddresses = result.addresses || [];
        setAddresses(foundAddresses);

        // Set no results found if addresses array is empty
        if (foundAddresses.length === 0) {
          setNoResultsFound(true);
        } else {
          setNoResultsFound(false);
        }

        setIsSearching(false);
      } catch (error) {
        console.error("Error fetching address:", error);
        setAddresses([]);
        setNoResultsFound(true);
        setIsSearching(false);
      }
    }, 1000);

    // Cleanup function
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [postalCode]);

  const handleAddressSelection = (address) => {
    // Set individual form values from address data
    registerForm.setValue("street", address.street || "");
    registerForm.setValue("buildingName", address.building_name || "");
    registerForm.setValue("buildingNumber", address.building_number || "");
    registerForm.setValue("flatNumber", address.flat_number || "");
    registerForm.setValue("town", address.town || "");
    registerForm.setValue("country", address.country || "");
    registerForm.setValue("postalCode", address.postcode || postalCode);

    // Clear the addresses dropdown and reset states
    setAddresses([]);
    setNoResultsFound(false);
  };

  // Updated form submission handler - DO NOT prevent default for password manager
  const onRegisterSubmit = async (values) => {
    console.log("Registering new user with values:", values);

    setIsSubmitting(true);

    try {
      await registerMutation.mutateAsync({
        ...values,
        id: savedUser?.id,
      });

      setTimeout(() => {
        window.scrollTo(0, 0);
        sessionStorage.removeItem("welcome_viewed");
        navigate("/welcome");
      }, 1000);
    } catch (error) {
      console.error("Registration failed:", error);
      setIsSubmitting(false);
    }
  };

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      console.log("User already logged in, checking first login status...");
      window.scrollTo(0, 0);
      if (user.isConsent) {
        navigate("/onboarding");
      } else {
        navigate("/welcome");
      }
    }
  }, [user, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-[#eaf1f3]">
      <NeedHelp />
      <div className="w-full max-w-md px-5 my-3">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <img
              src={logo}
              alt="In My Name KYC Logo"
              className="h-[6rem] w-auto"
            />
          </div>
        </div>

        <Form {...registerForm}>
          <form
            id="registerForm"
            ref={formRef}
            onSubmit={registerForm.handleSubmit(onRegisterSubmit)}
            method="post"
            action="/api/register"
          >
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={registerForm.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[#76ADAA] font-semibold">
                        First Name <span className="text-red-600">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="John"
                          {...field}
                          autoComplete="given-name"
                          className="bg-white border-0 rounded-[6px] h-12 px-4 text-[#243d3d] placeholder-[#948e8a] shadow-sm focus:ring-2 focus:ring-teal-500 focus:ring-offset-0"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={registerForm.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[#76ADAA] font-semibold">
                        Last Name <span className="text-red-600">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Smith"
                          {...field}
                          autoComplete="family-name"
                          className="bg-white border-0 rounded-[6px] h-12 px-4 text-[#243d3d] placeholder-[#948e8a] shadow-sm focus:ring-2 focus:ring-teal-500 focus:ring-offset-0"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={registerForm.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[#76ADAA] font-semibold">
                      Email <span className="text-red-600">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        autoComplete="username"
                        placeholder="john@example.com"
                        {...field}
                        className="bg-white border-0 rounded-[6px] h-12 px-4 text-[#243d3d] placeholder-[#948e8a] shadow-sm focus:ring-2 focus:ring-teal-500 focus:ring-offset-0"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={registerForm.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[#76ADAA] font-semibold">
                      Phone Number
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="+44 123 456 7890"
                        {...field}
                        autoComplete="tel"
                        className="bg-white border-0 rounded-[6px] h-12 px-4 text-[#243d3d] placeholder-[#948e8a] shadow-sm focus:ring-2 focus:ring-teal-500 focus:ring-offset-0"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={registerForm.control}
                name="postalCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[#76ADAA] font-semibold">
                      Postal Code <span className="text-red-600">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g. M1 1AA"
                        {...field}
                        autoComplete="postal-code"
                        onChange={(e) => {
                          field.onChange(e);
                          setPostalCode(e.target.value);
                        }}
                        className="bg-white border-0 rounded-[6px] h-12 px-4 text-[#243d3d] placeholder-[#948e8a] shadow-sm focus:ring-2 focus:ring-teal-500 focus:ring-offset-0"
                      />
                    </FormControl>
                    <FormMessage />

                    {/* Loading indicator while searching */}
                    {isSearching && postalCode.length >= 2 && (
                      <div className="mt-2 flex items-center text-sm text-[#76ADAA]">
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Searching for addresses...
                      </div>
                    )}

                    {/* Display addresses if available */}
                    {addresses.length > 0 && !isSearching && (
                      <div className="mt-2 max-h-40 overflow-y-auto border rounded-md bg-white shadow-lg z-10">
                        {addresses.map((address, index) => {
                          // Format the full address properly for display
                          const formatFullAddress = (addr) => {
                            const parts = [];

                            // Add flat number if exists
                            if (addr.flat_number && addr.flat_number.trim()) {
                              parts.push(addr.flat_number.trim());
                            }

                            // Add building number and name
                            const buildingParts = [];
                            if (
                              addr.building_number &&
                              addr.building_number.trim()
                            ) {
                              buildingParts.push(addr.building_number.trim());
                            }
                            if (
                              addr.building_name &&
                              addr.building_name.trim()
                            ) {
                              buildingParts.push(addr.building_name.trim());
                            }
                            if (buildingParts.length > 0) {
                              parts.push(buildingParts.join(" "));
                            }

                            // Add street
                            if (addr.street && addr.street.trim()) {
                              parts.push(addr.street.trim());
                            }

                            // Add sub street if exists
                            if (addr.sub_street && addr.sub_street.trim()) {
                              parts.push(addr.sub_street.trim());
                            }

                            // Add town
                            if (addr.town && addr.town.trim()) {
                              parts.push(addr.town.trim());
                            }
                            if (addr.country && addr.country.trim()) {
                              parts.push(addr.country.trim());
                            }

                            // Add postcode
                            if (addr.postcode && addr.postcode.trim()) {
                              parts.push(addr.postcode.trim());
                            }

                            return parts.join(", ");
                          };

                          return (
                            <div
                              key={index}
                              className="p-3 hover:bg-gray-100 cursor-pointer border-b last:border-b-0 transition-colors"
                              onClick={() => handleAddressSelection(address)}
                            >
                              <div className="text-sm text-gray-800">
                                {formatFullAddress(address)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Display no results message */}
                    {noResultsFound &&
                      !isSearching &&
                      postalCode.length >= 2 && (
                        <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
                          <div className="flex items-start">
                            <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 mr-2 flex-shrink-0" />
                            <div className="text-sm">
                              <div className="text-amber-800 font-medium">
                                Postal code not found
                              </div>
                              <div className="text-amber-700 mt-1">
                                No addresses found for "{postalCode}". Please
                                enter your address details manually in the
                                fields below.
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={registerForm.control}
                  name="street"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[#76ADAA] font-semibold">
                        Street
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="e.g. High Street"
                          {...field}
                          autoComplete="address-line1"
                          className="bg-white border-0 rounded-[6px] h-12 px-4 text-[#243d3d] placeholder-[#948e8a] shadow-sm focus:ring-2 focus:ring-teal-500 focus:ring-offset-0"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={registerForm.control}
                  name="buildingNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[#76ADAA] font-semibold">
                        Building/House Number
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="e.g. 42"
                          {...field}
                          className="bg-white border-0 rounded-[6px] h-12 px-4 text-[#243d3d] placeholder-[#948e8a] shadow-sm focus:ring-2 focus:ring-teal-500 focus:ring-offset-0"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={registerForm.control}
                  name="buildingName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[#76ADAA] font-semibold">
                        Building Name
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="e.g. Osprey House (optional)"
                          {...field}
                          className="bg-white border-0 rounded-[6px] h-12 px-4 text-[#243d3d] placeholder-[#948e8a] shadow-sm focus:ring-2 focus:ring-teal-500 focus:ring-offset-0"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={registerForm.control}
                  name="flatNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[#76ADAA] font-semibold">
                        Flat Number
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="e.g. Flat 4"
                          {...field}
                          className="bg-white border-0 rounded-[6px] h-12 px-4 text-[#243d3d] placeholder-[#948e8a] shadow-sm focus:ring-2 focus:ring-teal-500 focus:ring-offset-0"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={registerForm.control}
                  name="town"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[#76ADAA] font-semibold">
                        City/Town <span className="text-red-600">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="e.g. Manchester"
                          {...field}
                          autoComplete="address-level2"
                          className="bg-white border-0 rounded-[6px] h-12 px-4 text-[#243d3d] placeholder-[#948e8a] shadow-sm focus:ring-2 focus:ring-teal-500 focus:ring-offset-0"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={registerForm.control}
                  name="country"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[#76ADAA] font-semibold">
                        Country <span className="text-red-600">*</span>
                      </FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger className="bg-white border-0 rounded-[6px] h-12 px-4 text-[#243d3d] shadow-sm focus:ring-2 focus:ring-teal-500 focus:ring-offset-0">
                            <SelectValue placeholder="Select your country" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="GBR">United Kingdom</SelectItem>
                          <SelectItem value="IRL">Ireland</SelectItem>
                          <SelectItem value="USA">United States</SelectItem>
                          <SelectItem value="CAN">Canada</SelectItem>
                          <SelectItem value="AUS">Australia</SelectItem>
                          <SelectItem value="NZL">New Zealand</SelectItem>
                          <SelectItem value="FRA">France</SelectItem>
                          <SelectItem value="DEU">Germany</SelectItem>
                          <SelectItem value="ESP">Spain</SelectItem>
                          <SelectItem value="ITA">Italy</SelectItem>
                          <SelectItem value="NLD">Netherlands</SelectItem>
                          <SelectItem value="BEL">Belgium</SelectItem>
                          <SelectItem value="PRT">Portugal</SelectItem>
                          <SelectItem value="CHE">Switzerland</SelectItem>
                          <SelectItem value="SWE">Sweden</SelectItem>
                          <SelectItem value="NOR">Norway</SelectItem>
                          <SelectItem value="DNK">Denmark</SelectItem>
                          <SelectItem value="POL">Poland</SelectItem>
                          <SelectItem value="IND">India</SelectItem>
                          <SelectItem value="PAK">Pakistan</SelectItem>
                          <SelectItem value="ZAF">South Africa</SelectItem>
                          <SelectItem value="ARE">United Arab Emirates</SelectItem>
                          <SelectItem value="SGP">Singapore</SelectItem>
                          <SelectItem value="HKG">Hong Kong</SelectItem>
                          <SelectItem value="JPN">Japan</SelectItem>
                          <SelectItem value="CHN">China</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={registerForm.control}
                name="dateOfBirth"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[#76ADAA] font-semibold">
                      Date of Birth <span className="text-red-600">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="date of birth"
                        type="date"
                        {...field}
                        autoComplete="bday"
                        className="bg-white border-0 rounded-[6px] h-12 px-4 text-[#243d3d] placeholder-[#948e8a] shadow-sm focus:ring-2 focus:ring-teal-500 focus:ring-offset-0"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                type="submit"
                className="w-full bg-[#4c7375] hover:bg-teal-700 rounded-[6px] h-12 text-white font-semibold shadow-lg mt-6"
                disabled={registerMutation.isPending || isSubmitting}
              >
                {registerMutation.isPending || isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Registering...
                  </>
                ) : (
                  "REGISTER"
                )}
              </Button>
            </div>
          </form>
        </Form>

        <div className="mt-2">
          <div className="text-center">
            <span className="text-[#4c7375]">Already have an account? </span>
            <button
              className="text-[#243d3d] hover:text-[#4c7375] font-semibold underline"
              onClick={() => navigate("/login")}
            >
              Login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
